import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal/Modal";
import useAuth from "../../hooks/useAuth";
import { BASE_URL } from "../../utils/Element";
import AddContactData from "./components/AddContactData";

const initialData = {
  firstName: "",
  lastName: "",
  company_id: "",      // selected company ID
  company_name: "",    // display name
  company: "",
  title: "",
  description: "",
  mailingAddress: "",
  selectedLeadData: "",
  first_deal_anniversary: "",
  birthday: "",
  tax_record_sent: "",
  leadSourceType: "list",
  tags: [],
  isLead: false,
  contact_name: "",
};
const initialLinkData = [{ link_category: "", link: "" }];
const initialDateData = { date: "", month: "", year: "" };
const initialEmailData = [{ email_category: "work", email_id: "" }];
const initialRelationData = [{ relation_category: "", name: "", id: "" }];
const initialAddressData = [{ address_category: "work", address: "", apt_unit_suite: "" }];
const initialPhoneData = [{ phone_category: "mobile", country_code: "+1", phone_number: "", ext: "" }];

const EditContactModal = ({ showModal, onClose, onSetCallApiAgain, from = "contact" }) => {
  const { id } = useParams();
  const [config] = useAuth();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(true);
  const [disable, setDisable] = useState(false);
  const [links, setLinks] = useState(initialLinkData);
  const [phones, setPhones] = useState(initialPhoneData);
  const [emails, setEmails] = useState(initialEmailData);
  const [birthday, setBirthday] = useState(initialDateData);
  const [taxRecord, setTaxRecord] = useState(initialDateData);
  const [addresses, setAddresses] = useState(initialAddressData);
  const [anniversary, setAnniversary] = useState(initialDateData);
  const [relations, setRelations] = useState(initialRelationData);
  const [addContactData, setAddContactData] = useState(initialData);

  const handleClose = () => {
    setDisable(false);
    onClose();
    setPhones(initialPhoneData);
    setAddContactData(initialData);
    setAddresses(initialAddressData);
    setTaxRecord(initialDateData);
    setAnniversary(initialDateData);
    setBirthday(initialDateData);
    setLinks(initialLinkData);
    setEmails(initialEmailData);
    setRelations(initialRelationData);
    setError();
  };

  const fetchContactDetails = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/contact-getbyid/${id}`, config)
      .then((res) => {
        const contactDetails = res?.data?.data;

        setAddresses(contactDetails?.address?.length ? contactDetails?.address : initialAddressData);
        setPhones(contactDetails?.phone?.length ? contactDetails?.phone : initialPhoneData);
        setLinks(contactDetails?.links?.length ? contactDetails?.links : initialLinkData);
        setEmails(contactDetails?.email?.length ? contactDetails?.email : initialEmailData);
        setRelations(contactDetails?.relationship?.length ? contactDetails?.relationship : initialRelationData);

        setTaxRecord({
          date: moment(contactDetails?.tax_record_sent).date(),
          month: moment(contactDetails?.tax_record_sent).month() + 1,
          year: moment(contactDetails?.tax_record_sent).year(),
        });
        setAnniversary({
          date: moment(contactDetails?.first_deal_anniversary).date(),
          month: moment(contactDetails?.first_deal_anniversary).month() + 1,
          year: moment(contactDetails?.first_deal_anniversary).year(),
        });
        setBirthday({
          date: moment(contactDetails?.birthday).date(),
          month: moment(contactDetails?.birthday).month() + 1,
          year: moment(contactDetails?.birthday).year(),
        });

        setAddContactData({
          firstName: contactDetails?.first_name,
          lastName: contactDetails?.last_name,
          description: contactDetails?.description,
          title: contactDetails?.title,
          company: contactDetails?.company_id,
          company_name: contactDetails?.company?.company_name,
          company: contactDetails?.company?.company,
          selectedLeadData: contactDetails?.lead_source?.id,
          tax_record_sent: contactDetails?.tax_record_sent,
          birthday: contactDetails?.birthday,
          first_deal_anniversary: contactDetails?.first_deal_anniversary,
          leadSourceType: contactDetails?.select_lead_type,
          tags: contactDetails?.tags?.map((el) => el?.id),
          isLead: contactDetails?.mark_as_lead == 0 ? false : true,
          contact_name: contactDetails?.contact_source?.name,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id && showModal) {
      fetchContactDetails();
    }
  }, [id, showModal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setDisable(true);
    const dataToSend = {
      mark_as_lead: addContactData?.isLead ? 1 : 0,
      first_name: addContactData?.firstName,
      last_name: addContactData?.lastName,
      phone: phones,
      email: emails,
      address: addresses,
      tag_ids: addContactData?.tags,
      relationship: relations,
      links: links,
      company_id: addContactData?.company,
      company_name: addContactData?.company_name,
      company: addContactData?.company || "",
      title: addContactData?.title,
      first_deal_anniversary: addContactData?.first_deal_anniversary,
      birthday: addContactData?.birthday,
      tax_record_sent: addContactData?.tax_record_sent,
      description: addContactData?.description,
      select_lead_type: addContactData?.leadSourceType,
      lead_source_contact_id: addContactData?.leadSourceType === "contact" ? addContactData?.selectedLeadData : "",
      leadsource_id: addContactData?.leadSourceType === "list" ? addContactData?.selectedLeadData : "",
      select_contact: 0,
      contact_source: "",
    };

    axios
      .put(`${BASE_URL}/contact-edit/${id}`, dataToSend, config)
      .then((res) => {
        handleClose();
        onSetCallApiAgain();
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        setDisable(false);
        setError(err.response?.data?.errors);
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  return (
    <Modal title={"Edit Contact"} desc={"Edit the Contact information."} width="580px" show={showModal} onClose={handleClose}>
      {loading ? (
        <Loader />
      ) : (
        <AddContactData
          addContactData={addContactData}
          phones={phones}
          onSetPhones={(value) => setPhones(value)}
          emails={emails}
          onSetEmails={(value) => setEmails(value)}
          addresses={addresses}
          onSetAddresses={(value) => setAddresses(value)}
          onClose={handleClose}
          handleSubmit={handleSubmit}
          onSetAddContactData={(value) => setAddContactData(value)}
          relations={relations}
          onSetRelations={(value) => setRelations(value)}
          links={links}
          onSetLinks={(value) => setLinks(value)}
          anniversary={anniversary}
          onSetAnniversary={(value) => setAnniversary(value)}
          birthday={birthday}
          onSetBirthday={(value) => setBirthday(value)}
          taxRecord={taxRecord}
          onSetTaxRecord={(value) => setTaxRecord(value)}
          disable={disable}
          showModal={showModal}
          error={error}
          from={from}
          onSetError={(value) => setError(value)}
        />
      )}
    </Modal>
  );
};

export default EditContactModal;
